import * as React from "react";
import { SpaceBetween, Box, FormField, Select, Multiselect, Textarea, Button, Header, Container, TokenGroup, TextContent, Toggle } from "@amzn/awsui-components-react";
import { useAPI } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ContentEvaluationConfiguration, IContentEvaluation, Container as ContainerData, APIOutput } from "@amzn/ask-legal-domain";

export interface PagePreferencesTabProps {
    pageId: string;
    containerIds: string[];
    containerMetadata?: {[key: string]: ContainerData.Data};
}

export const PagePreferencesTab = (props: PagePreferencesTabProps) => {
    const context = React.useContext(AppContext);
    const loadConfigRunner = useAPI(
        context.getContentEvaluationConfigurationAPI().load
    );
    const createConfigRunner = useAPI(
        context.getContentEvaluationConfigurationAPI().create
    );
    const updateConfigRunner = useAPI(
        context.getContentEvaluationConfigurationAPI().update
    );

    const [evaluationGranularity, setEvaluationGranularity] = React.useState<ContentEvaluationConfiguration.EvaluationGranularity>(
        ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage
    );
    const [selectedContainerIds, setSelectedContainerIds] = React.useState<string[]>([]);
    const [customPromptSection, setCustomPromptSection] = React.useState<string>("");
    const [deprecated, setDeprecated] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [existingConfig, setExistingConfig] = React.useState<ContentEvaluationConfiguration.Data | null>(null);
    const [isSaved, setIsSaved] = React.useState<boolean>(false);
    const [editing, setEditing] = React.useState<boolean>(false);
    const [loadedContainerMetadata, setLoadedContainerMetadata] = React.useState<{[key: string]: ContainerData.Data}>({});
    const [loadingMetadata, setLoadingMetadata] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadConfigRunner.submitRun(props.pageId);
    }, [props.pageId]);

    // Load container metadata using batchLoadMetadata
    React.useEffect(() => {
        if (props.containerMetadata && Object.keys(props.containerMetadata).length > 0) {
            setLoadingMetadata(true);

            const containerRefs = Object.values(props.containerMetadata);

            context.getContainerAPI().batchLoadMetadata(containerRefs)
                .then(rawOutput => {
                    const output = APIOutput.fromRaw<ContainerData.Data[]>(rawOutput.data);
                    if (!output.isErr()) {
                        const metadataMap = output.data.reduce((map, container) => {
                            map[container.id] = container;
                            return map;
                        }, {} as {[key: string]: ContainerData.Data});
                        setLoadedContainerMetadata(metadataMap);
                    } else {
                        console.error("Error loading container metadata:", output.err);
                        // Fallback to props.containerMetadata if API fails
                        setLoadedContainerMetadata(props.containerMetadata);
                    }
                })
                .catch(error => {
                    console.error("Error loading container metadata:", error);
                    // Fallback to props.containerMetadata if API fails
                    setLoadedContainerMetadata(props.containerMetadata);
                })
                .finally(() => {
                    setLoadingMetadata(false);
                });
        }
    }, [props.containerMetadata]);

    React.useEffect(() => {
        if (loadConfigRunner.status === "Succeeded") {
            const config = loadConfigRunner.data.output;
            setExistingConfig(config);
            setEvaluationGranularity(config.evaluationGranularity);
            setSelectedContainerIds(config.containerIds);
            setCustomPromptSection(config.customPromptSection);
            setDeprecated(config.deprecated ?? false);
            setIsSaved(true);
        } else if (loadConfigRunner.status === "Error") {
            // Config doesn't exist yet, set defaults
            setExistingConfig(null);
            setEvaluationGranularity(ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage);
            setSelectedContainerIds(props.containerIds);
            setCustomPromptSection("");
            setDeprecated(false);
            setIsSaved(false);
        }
    }, [loadConfigRunner.status, props.containerIds]);

    React.useEffect(() => {
        if (createConfigRunner.status === "Succeeded" || updateConfigRunner.status === "Succeeded") {
            setIsLoading(false);
            setEditing(false);
            // Reload the configuration to get the latest data
            loadConfigRunner.submitRun(props.pageId);
            // Note: Don't set isSaved directly here - let the tracking effect handle it
            // after the config is reloaded and existingConfig is updated
        }
    }, [createConfigRunner.status, updateConfigRunner.status, props.pageId]);

    // Track if there are unsaved changes
    React.useEffect(() => {
        if (existingConfig) {
            const hasChanges =
                evaluationGranularity !== existingConfig.evaluationGranularity ||
                JSON.stringify(selectedContainerIds.sort()) !== JSON.stringify(existingConfig.containerIds.sort()) ||
                customPromptSection !== existingConfig.customPromptSection ||
                deprecated !== (existingConfig.deprecated ?? false);
            setIsSaved(!hasChanges);
        } else {
            // If no existing config, consider it unsaved if any values are set
            setIsSaved(false);
        }
    }, [evaluationGranularity, selectedContainerIds, customPromptSection, deprecated, existingConfig]);

    const handleSave = () => {
        setIsLoading(true);

        // Only include containerIds when evaluation granularity is per container
        const containerIds = evaluationGranularity === ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage
            ? []  // Empty array for page-level evaluation
            : selectedContainerIds;

        if (existingConfig) {
            // Update existing configuration
            const updateInput = IContentEvaluation.UpdateContentEvaluationConfigurationInput.create({
                pageId: props.pageId,
                containerIds: containerIds,
                evaluationGranularity: evaluationGranularity,
                customPromptSection: customPromptSection,
                deprecated: deprecated
            });
            updateConfigRunner.submitRun(updateInput);
        } else {
            // Create new configuration
            const createInput = IContentEvaluation.CreateContentEvaluationConfigurationInput.create({
                pageId: props.pageId,
                containerIds: containerIds,
                evaluationGranularity: evaluationGranularity,
                customPromptSection: customPromptSection
            });
            createConfigRunner.submitRun(createInput);
        }
    };

    const containerOptions = props.containerIds.map(id => {
        const container = loadedContainerMetadata[id];
        const title = container?.title?.trim();
        const label = title && title.length > 0 ? title : id;
        return {
            label: label,
            value: id
        };
    });

    const granularityOptions = [
        {
            label: "Evaluate per page",
            value: ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage,
            description: "Show a single evaluation button at the page level"
        },
        {
            label: "Evaluate per container",
            value: ContentEvaluationConfiguration.EvaluationGranularity.EvaluationPerContainer,
            description: "Show evaluation buttons on each enabled container"
        }
    ];

    // Only show UI after the configuration loading request has completed
    if (loadConfigRunner.status === "Running" || loadConfigRunner.status === "New") {
        return (
            <Container>
                <Box textAlign="center" padding="l">
                    Loading configuration...
                </Box>
            </Container>
        );
    }

    const empty = (
        <Box padding={{ left: "xxxl" }} textAlign="center">
            <TextContent>
                <em>
                    <small>
                        --None--
                    </small>
                </em>
            </TextContent>
        </Box>
    );

    return (
        <Container
            header={
                <Header
                    variant="h3"
                    description="Configure content evaluation settings for this page"
                    actions={
                        editing ? (
                            <SpaceBetween direction="horizontal" size="m">
                                <Button
                                    loading={createConfigRunner.status === "Running" || updateConfigRunner.status === "Running" || isLoading}
                                    onClick={() => {
                                        setEditing(false);
                                        // Reset to original values
                                        if (existingConfig) {
                                            setEvaluationGranularity(existingConfig.evaluationGranularity);
                                            setSelectedContainerIds(existingConfig.containerIds);
                                            setCustomPromptSection(existingConfig.customPromptSection);
                                            setDeprecated(existingConfig.deprecated ?? false);
                                        }
                                    }}
                                    variant="normal">Cancel
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    variant="primary"
                                    disabled={isSaved}
                                    loading={createConfigRunner.status === "Running" || updateConfigRunner.status === "Running" || isLoading}>
                                    {existingConfig ? "Save" : "Create Configuration"}
                                </Button>
                            </SpaceBetween>
                        ) : (
                            <Button onClick={() => setEditing(true)}>Edit</Button>
                        )
                    }>
                    Content Evaluation Preferences
                </Header>
            }>
            <SpaceBetween size="l">
                <FormField
                    label="Evaluation Granularity"
                    description="Choose whether to evaluate content at the page level or per container"
                >
                    {editing ? (
                        <Select
                            selectedOption={granularityOptions.find(option => option.value === evaluationGranularity)}
                            onChange={({ detail }) =>
                                setEvaluationGranularity(detail.selectedOption.value as ContentEvaluationConfiguration.EvaluationGranularity)
                            }
                            options={granularityOptions}
                            disabled={isLoading}
                        />
                    ) : (
                        <Box padding={{ left: "xxxl" }}>
                            <TextContent>
                                {granularityOptions.find(option => option.value === evaluationGranularity)?.label || "Not configured"}
                            </TextContent>
                        </Box>
                    )}
                </FormField>

                {evaluationGranularity !== ContentEvaluationConfiguration.EvaluationGranularity.EvalautionPerPage && (
                    <FormField
                        label="Enabled Containers"
                        description="Select which containers should have content evaluation enabled"
                    >
                        {editing ? (
                            <Multiselect
                                selectedOptions={selectedContainerIds.map(id => {
                                    const container = loadedContainerMetadata[id];
                                    const title = container?.title?.trim();
                                    return {
                                        label: title && title.length > 0 ? title : id,
                                        value: id
                                    };
                                })}
                                onChange={({ detail }) =>
                                    setSelectedContainerIds(detail.selectedOptions.map(option => option.value))
                                }
                                options={containerOptions}
                                disabled={isLoading || loadingMetadata}
                                placeholder="Select containers"
                            />
                        ) : (
                            selectedContainerIds.length > 0 ? (
                                <TokenGroup
                                    items={selectedContainerIds.map(id => {
                                        const container = loadedContainerMetadata[id];
                                        const title = container?.title?.trim();
                                        return {
                                            label: title && title.length > 0 ? title : id,
                                            disabled: !editing
                                        };
                                    })}
                                />
                            ) : empty
                        )}
                    </FormField>
                )}

                <FormField
                    label="Content Evaluation Status"
                    description="Enable or disable the content evaluation feature for this page"
                >
                    {editing ? (
                        <Toggle
                            onChange={({ detail }) => setDeprecated(!detail.checked)}
                            checked={!deprecated}
                            disabled={isLoading}
                        >
                            {deprecated ? "Disabled" : "Enabled"}
                        </Toggle>
                    ) : (
                        <Box padding={{ left: "xxxl" }}>
                            <TextContent>
                                {deprecated ? "Disabled" : "Enabled"}
                            </TextContent>
                        </Box>
                    )}
                </FormField>

            </SpaceBetween>
        </Container>
    );
};
